import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import apiClient from '../../services/api';
import './StocktakeDetails.css';

const StocktakeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [stocktake, setStocktake] = useState(null);
  const [stocktakeItems, setStocktakeItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterVariance, setFilterVariance] = useState('all');
  const [adjusting, setAdjusting] = useState(false);

  useEffect(() => {
    fetchStocktakeData();
  }, [id]);

  const fetchStocktakeData = async () => {
    try {
      setLoading(true);
      
      const [stocktakeResponse, itemsResponse] = await Promise.all([
        apiClient.get(`/stocktake/${id}`),
        apiClient.get(`/stocktake/${id}/items`)
      ]);
      
      setStocktake(stocktakeResponse.data);
      setStocktakeItems(itemsResponse.data);
    } catch (error) {
      console.error('Error fetching stocktake data:', error);
      setError('Failed to load stocktake data');
    } finally {
      setLoading(false);
    }
  };

  const handleAdjustInventory = async () => {
    if (window.confirm('This will adjust inventory quantities based on stocktake results. Continue?')) {
      try {
        setAdjusting(true);
        await apiClient.post(`/stocktake/${id}/adjust-inventory`, {});
        
        alert('Inventory has been adjusted successfully!');
        fetchStocktakeData(); // Refresh data
      } catch (error) {
        console.error('Error adjusting inventory:', error);
        setError('Failed to adjust inventory');
      } finally {
        setAdjusting(false);
      }
    }
  };

  const getFilteredItems = () => {
    let filtered = stocktakeItems;
    
    if (searchTerm) {
      filtered = filtered.filter(item => 
        item.item?.itemName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.item?.itemCode?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    if (filterVariance !== 'all') {
      filtered = filtered.filter(item => {
        const variance = item.variance || 0;
        switch (filterVariance) {
          case 'positive': return variance > 0;
          case 'negative': return variance < 0;
          case 'zero': return variance === 0;
          default: return true;
        }
      });
    }
    
    return filtered;
  };

  const getStocktakeStats = () => {
    const total = stocktakeItems.length;
    const counted = stocktakeItems.filter(item => item.status === 'Counted').length;
    const variances = stocktakeItems.filter(item => item.variance && item.variance !== 0).length;
    const totalVariance = stocktakeItems.reduce((sum, item) => sum + (item.variance || 0), 0);
    
    return { total, counted, variances, totalVariance };
  };

  const getStatusBadge = (status) => {
    const statusClass = {
      'Pending': 'status-pending',
      'InProgress': 'status-in-progress',
      'Completed': 'status-completed'
    };
    return <span className={`status-badge ${statusClass[status] || ''}`}>{status}</span>;
  };

  const getVarianceBadge = (variance) => {
    if (!variance || variance === 0) {
      return <span className="variance-badge variance-zero">0</span>;
    }
    
    const className = variance > 0 ? 'variance-positive' : 'variance-negative';
    const sign = variance > 0 ? '+' : '';
    
    return <span className={`variance-badge ${className}`}>{sign}{variance}</span>;
  };

  if (loading) return <div className="loading">Loading stocktake details...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!stocktake) return <div className="error">Stocktake not found</div>;

  const filteredItems = getFilteredItems();
  const stats = getStocktakeStats();

  return (
    <div className="stocktake-details">
      <div className="details-header">
        <div className="header-info">
          <h1>{stocktake.stocktakeName}</h1>
          <div className="header-meta">
            {getStatusBadge(stocktake.status)}
            <span className="date-info">
              Started: {new Date(stocktake.startDate).toLocaleDateString()}
            </span>
            {stocktake.endDate && (
              <span className="date-info">
                Completed: {new Date(stocktake.endDate).toLocaleDateString()}
              </span>
            )}
          </div>
          {stocktake.description && (
            <p className="description">{stocktake.description}</p>
          )}
        </div>
        
        <div className="header-actions">
          <button 
            onClick={() => navigate('/inventory/stocktakes')}
            className="btn btn-secondary"
          >
            Back to List
          </button>
          
          {stocktake.status === 'InProgress' && (
            <Link 
              to={`/inventory/stocktakes/${id}/conduct`}
              className="btn btn-primary"
            >
              Continue Counting
            </Link>
          )}
          
          {stocktake.status === 'Completed' && stats.variances > 0 && (
            <button 
              onClick={handleAdjustInventory}
              disabled={adjusting}
              className="btn btn-warning"
            >
              {adjusting ? 'Adjusting...' : 'Adjust Inventory'}
            </button>
          )}
        </div>
      </div>

      <div className="stats-section">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Items</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.counted}</div>
            <div className="stat-label">Items Counted</div>
          </div>
          <div className="stat-card">
            <div className="stat-number">{stats.variances}</div>
            <div className="stat-label">Variances Found</div>
          </div>
          <div className="stat-card">
            <div className={`stat-number ${stats.totalVariance > 0 ? 'positive' : stats.totalVariance < 0 ? 'negative' : ''}`}>
              {stats.totalVariance > 0 ? '+' : ''}{stats.totalVariance}
            </div>
            <div className="stat-label">Total Variance</div>
          </div>
        </div>
      </div>

      <div className="filters-section">
        <div className="search-filter">
          <input
            type="text"
            placeholder="Search items..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="variance-filter">
          <select 
            value={filterVariance} 
            onChange={(e) => setFilterVariance(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Variances</option>
            <option value="positive">Positive Variances</option>
            <option value="negative">Negative Variances</option>
            <option value="zero">No Variance</option>
          </select>
        </div>
      </div>

      <div className="items-section">
        {filteredItems.length === 0 ? (
          <div className="no-items">
            <p>No items found matching your criteria.</p>
          </div>
        ) : (
          <div className="items-table">
            <div className="table-header">
              <div className="col-item">Item</div>
              <div className="col-system">System Qty</div>
              <div className="col-counted">Counted Qty</div>
              <div className="col-variance">Variance</div>
              <div className="col-status">Status</div>
              <div className="col-counted-by">Counted By</div>
            </div>
            
            {filteredItems.map(item => (
              <div key={item.stocktakeItemId} className="table-row">
                <div className="col-item">
                  <div className="item-info">
                    <div className="item-name">{item.item?.itemName}</div>
                    <div className="item-code">{item.item?.itemCode}</div>
                  </div>
                </div>
                <div className="col-system">{item.systemQuantity}</div>
                <div className="col-counted">
                  {item.countedQuantity !== null ? item.countedQuantity : '-'}
                </div>
                <div className="col-variance">
                  {item.countedQuantity !== null ? getVarianceBadge(item.variance) : '-'}
                </div>
                <div className="col-status">
                  <span className={`item-status ${item.status.toLowerCase()}`}>
                    {item.status}
                  </span>
                </div>
                <div className="col-counted-by">
                  {item.countedByUser ? 
                    `${item.countedByUser.firstName} ${item.countedByUser.lastName}` : 
                    '-'
                  }
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StocktakeDetails;